/**
 * The auth gate (SPEC.md §2.2, §11 item 16, DECISIONS.md D189) — supersedes the old bearer-
 * token prompt.
 *
 * Wrapping the whole app in this means no route can render without an authenticated
 * account, and the shared `UNAUTHORIZED_EVENT`/`LOGGED_OUT_EVENT` listeners mean no route
 * can forget to handle a session ending, whichever way it ends.
 *
 * Bearer tokens haven't gone away — `TokensCard` still mints them for non-browser clients,
 * and a token handed over by QR (`#t=…`) is still adopted here exactly as it always was, so
 * a browser someone opens that link in still gets straight in. But the browser's own,
 * primary way in is a session now: a username and password, or an invite code for a friend
 * who's never had an account (§2.2: invite-only is a gate in front of `/auth/register`, not
 * a property of `users` — this is that gate's UI).
 */

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  ApiError,
  LOGGED_OUT_EVENT,
  UNAUTHORIZED_EVENT,
  adoptTokenFromUrl,
  api,
  type Me,
} from "../api";
import { clearReadCache } from "../readCache";

type Mode = "login" | "register";

export function AuthGate({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A token in the fragment wins over whatever is stored: arriving by QR is an explicit
    // instruction to use *this* credential (unchanged from the old TokenGate).
    adoptTokenFromUrl();
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const dropSession = () => {
      setMe(null);
      // Whatever the last account could see is not necessarily what the next one may see.
      clearReadCache();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, dropSession);
    window.addEventListener(LOGGED_OUT_EVENT, dropSession);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, dropSession);
      window.removeEventListener(LOGGED_OUT_EVENT, dropSession);
    };
  }, []);

  if (loading) {
    return (
      <div className="gate">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (me) return <>{children}</>;
  return <LoginOrRegister onAuthenticated={setMe} />;
}

function LoginOrRegister({ onAuthenticated }: { onAuthenticated: (me: Me) => void }) {
  // A link shared as `?invite=CODE` lands here pre-filled and already on the register tab —
  // the code itself is single-use and not bound to any one username (D192), so it's not
  // treated with the same never-touches-the-server care as a bearer token's URL *fragment*
  // (`tokenUrl.ts`).
  const invited = new URLSearchParams(window.location.search).get("invite");
  const [mode, setMode] = useState<Mode>(invited ? "register" : "login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(invited ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const account =
        mode === "login"
          ? await api.login(username, password)
          : await api.register(username, password, inviteCode.trim());
      onAuthenticated(account);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setSubmitting(false);
    }
  }

  const ready = Boolean(
    username.trim() && password && (mode === "login" || inviteCode.trim()),
  );

  return (
    <div className="gate">
      <h1>eFss</h1>
      {mode === "login" ? (
        <p>Log in with the account you already have.</p>
      ) : (
        <p>
          Register with the invite you were sent - ask whoever invited you for the code if
          this wasn't filled in for you.
        </p>
      )}

      <form onSubmit={submit}>
        <input
          className="input"
          type="text"
          autoComplete="username"
          placeholder="Username"
          aria-label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          className="input"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          placeholder="Password"
          aria-label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {mode === "register" && (
          <input
            className="input"
            autoComplete="off"
            spellCheck={false}
            placeholder="Invite code"
            aria-label="Invite code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
          />
        )}
        {error && <p className="error">{error}</p>}
        <button className="btn primary" type="submit" disabled={submitting || !ready}>
          {submitting ? "Checking…" : mode === "login" ? "Log in" : "Register"}
        </button>
      </form>

      <p className="small">
        {mode === "login" ? (
          <>
            Have an invite instead?{" "}
            <button
              type="button"
              className="btn ghost small"
              onClick={() => setMode("register")}
            >
              Register
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button type="button" className="btn ghost small" onClick={() => setMode("login")}>
              Log in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
